// src/utils/api.ts
import axios from 'axios';

// Use relative path for API - nginx will proxy /api/ to backend
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: any) => {
    const requestUrl = String(config.url || '');
    const isAuthAttempt =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/register') ||
      requestUrl.includes('/auth/forgot-password') ||
      requestUrl.includes('/auth/reset-password');

    if (!isAuthAttempt) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Let the browser set multipart boundary for FormData
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers) {
        if (typeof headers.delete === 'function') {
          headers.delete('Content-Type');
          headers.delete('content-type');
        } else {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
        // axios may re-apply common defaults — force unset
        if (typeof headers.set === 'function') {
          headers.set('Content-Type', undefined as any);
        }
      }
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor — logout only for expired session, not failed login attempts
api.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      const isAuthAttempt =
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/register');

      if (!isAuthAttempt) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_data');
        window.dispatchEvent(new Event('auth-session-expired'));

        const path = window.location.pathname;
        const isPublicPage =
          path === '/login' ||
          path === '/register' ||
          path === '/' ||
          path.startsWith('/verify-email') ||
          path.startsWith('/forgot-password') ||
          path.startsWith('/reset-password') ||
          path === '/privacy-policy';

        if (!isPublicPage) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const doctorsAPI = {
  async getDashboard() {
    return api.get('/doctors/dashboard');
  },
  async getPatients(params?: {
    sort_by?: 'last_activity' | 'due_date' | 'week_of_pregnancy' | 'full_name';
    order?: 'asc' | 'desc';
    paused_only?: boolean;
    access_level?: string;
    q?: string;
  }) {
    return api.get('/doctors/patients', { params });
  },
  async assignPatient(patientEmail: string) {
    return api.post('/doctors/assign-patient', null, {
      params: { patient_email: patientEmail }
    });
  },
  async getPatientDetails(patientId: number) {
    return api.get(`/doctors/patients/${patientId}`);
  },
  async getPatientSummary(patientId: number) {
    return api.get(`/doctors/patients/${patientId}/summary`);
  },
  async getPatientLatestMetrics(patientId: number) {
    return api.get(`/doctors/patients/${patientId}/latest-metrics`);
  },
  async getPatientDiary(patientId: number, date?: string) {
    const params = date ? { date } : {};
    return api.get(`/doctors/patients/${patientId}/diary`, { params });
  },
  async getPatientGlucoseEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/glucose-entries`, { params });
  },
  async getPatientWeightEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/weight-entries`, { params });
  },
  async getPatientMovementEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/movement-entries`, { params });
  },
  async getPatientSymptomEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/symptom-entries`, { params });
  },
  async getPatientAbdomenEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/abdomen-entries`, { params });
  },
  async getPatientVisitEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/visit-entries`, { params });
  },
  async getPatientLabTestEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/lab-test-entries`, { params });
  },
  async getPatientMealEntries(patientId: number, params?: any) {
    return api.get(`/doctors/patients/${patientId}/meal-entries`, { params });
  },
};

export const doctorLinkAPI = {
  getDoctorLinkState: () => api.get('/me/doctor-link'),
  getClinics: () => api.get('/me/doctor-link/clinics'),
  searchDoctors: (params?: { clinic_id?: number; q?: string }) =>
    api.get('/me/doctor-search', { params }),
  requestDoctorLink: (payload: {
    doctor_public_id: string;
    access_level: 'ultrasound_only' | 'ultrasound_labs' | 'full';
  }) => api.post('/me/doctor-link/request', payload),
  patchDoctorLink: (payload: {
    access_level?: 'ultrasound_only' | 'ultrasound_labs' | 'full';
    is_data_hidden_temporarily?: boolean;
  }) => api.patch('/me/doctor-link', payload),
  revokeDoctorLink: () => api.post('/me/doctor-link/revoke'),
  doctorIncomingLinks: () => api.get('/doctors/incoming-links'),
  doctorAcceptLink: (linkId: string) => api.post(`/doctors/links/${linkId}/accept`),
  doctorRejectLink: (linkId: string, payload?: { comment?: string }) =>
    api.post(`/doctors/links/${linkId}/reject`, payload || {}),
  doctorArchiveLink: (linkId: string, payload?: { comment?: string }) =>
    api.post(`/doctors/links/${linkId}/archive`, payload || {}),
  getDoctorQr: () => api.get('/me/doctor-qr'),
};

export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  registerPatient: (userData: any) =>
    api.post('/auth/register/patient', userData),

  verifyEmail: (token: string) =>
    api.get('/auth/verify-email', {
      params: { token }
    }).catch((error) => {
      // Handle error properly
      throw error;
    }),

  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', null, {
      params: { email }
    }),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', null, {
      params: { email }
    }),

  resetPassword: (token: string, newPassword: string) =>
    api.post('/auth/reset-password', null, {
      params: { token, new_password: newPassword }
    }),

  getProfile: (config?: { signal?: AbortSignal }) =>
    api.get('/me', config as any),

  updateProfile: (userData: any) =>
    api.put('/me', userData),
  
  updateProfilePartial: (formData: FormData) => api.patch('/me', formData),

  deleteAccount: () => api.delete('/me'),
};

export const gamificationAPI = {
  getAchievements: () =>
    api.get('/gamification/achievements'),

  getBalance: () =>
    api.get('/gamification/balance'),

  getTransactions: (limit?: number) =>
    api.get('/gamification/transactions', {
      params: { limit }
    }),

  getRewards: () =>
    api.get('/gamification/rewards'),

  purchaseReward: (rewardId: number) =>
    api.post(`/gamification/rewards/${rewardId}/purchase`),

  getMyRewards: () =>
    api.get('/gamification/my-rewards'),
};

export const adminAPI = {
  getUsers: (params?: any) =>
    api.get('/admin/users', { params }),

  getClinics: () => api.get('/admin/clinics'),

  createClinic: (data: {
    name: string;
    slug?: string;
    city?: string;
    contact_phone?: string;
    contact_email?: string;
  }) => api.post('/admin/clinics', data),

  updateClinic: (
    clinicId: number,
    data: {
      name?: string;
      slug?: string;
      city?: string | null;
      contact_phone?: string | null;
      contact_email?: string | null;
    }
  ) => api.put(`/admin/clinics/${clinicId}`, data),

  deleteClinic: (clinicId: number) => api.delete(`/admin/clinics/${clinicId}`),

  createUser: (userData: any) => api.post('/admin/users', userData),

  createDoctor: (userData: any) =>
    api.post('/admin/doctors', userData),

  updateUser: (userId: number, userData: any) =>
    api.put(`/admin/users/${userId}`, userData),

  deleteUser: (userId: number) =>
    api.delete(`/admin/users/${userId}`),

  resetPassword: (userId: number, newPassword: string) => {
    const body = new URLSearchParams();
    body.append('new_password', newPassword);
    return api.post(`/admin/users/${userId}/reset-password`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },

  assignPatientToDoctor: (doctorId: number, patientId: number) => {
    const body = new URLSearchParams();
    body.append('patient_id', String(patientId));
    return api.post(`/admin/doctors/${doctorId}/assign-patient`, body.toString(), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });
  },

  unassignPatientFromDoctor: (doctorId: number, patientId: number) =>
    api.delete(`/admin/doctors/${doctorId}/patients/${patientId}`),

  forceRevokePatientDoctorLink: (patientId: number, reason?: string) =>
    api.post(`/admin/patients/${patientId}/doctor-link/force-revoke`, reason ? { reason } : {}),

  getDoctorPatients: (doctorId: number) =>
    api.get(`/admin/doctors/${doctorId}/patients`),

  getUserDetails: (userId: number) =>
    api.get(`/admin/users/${userId}`),

  getPatientGlucoseEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/glucose-entries`, { params }),
  getPatientMealEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/meal-entries`, { params }),
  getPatientWeightEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/weight-entries`, { params }),
  getPatientMovementEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/movement-entries`, { params }),
  getPatientSymptomEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/symptom-entries`, { params }),
  getPatientAbdomenEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/abdomen-entries`, { params }),
  getPatientVisitEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/visit-entries`, { params }),
  getPatientLabTestEntries: (patientId: number, params?: any) =>
    api.get(`/admin/patients/${patientId}/lab-test-entries`, { params }),

  // Admin diary editing
  updateGlucoseEntry: (entryId: number, data: any) =>
    api.put(`/admin/diary/glucose/${entryId}`, data),

  deleteGlucoseEntry: (entryId: number) =>
    api.delete(`/admin/diary/glucose/${entryId}`),

  updateMealEntry: (entryId: number, data: any) =>
    api.put(`/admin/diary/meals/${entryId}`, data),

  deleteMealEntry: (entryId: number) =>
    api.delete(`/admin/diary/meals/${entryId}`),

  updateWeightEntry: (entryId: number, data: any) =>
    api.put(`/admin/diary/weight/${entryId}`, data),

  deleteWeightEntry: (entryId: number) =>
    api.delete(`/admin/diary/weight/${entryId}`),

  updateMovementEntry: (entryId: number, data: any) =>
    api.put(`/admin/diary/movements/${entryId}`, data),

  deleteMovementEntry: (entryId: number) =>
    api.delete(`/admin/diary/movements/${entryId}`),

  updateSymptomEntry: (entryId: number, data: any) =>
    api.put(`/admin/diary/symptoms/${entryId}`, data),

  deleteSymptomEntry: (entryId: number) =>
    api.delete(`/admin/diary/symptoms/${entryId}`),

  updateAbdomenEntry: (entryId: number, formData: FormData) =>
    api.put(`/admin/diary/abdomen/${entryId}`, formData),

  deleteAbdomenEntry: (entryId: number) =>
    api.delete(`/admin/diary/abdomen/${entryId}`),

  updateVisitEntry: (entryId: number, formData: FormData) =>
    api.put(`/admin/diary/visits/${entryId}`, formData),

  deleteVisitEntry: (entryId: number) =>
    api.delete(`/admin/diary/visits/${entryId}`),

  updateLabTestEntry: (entryId: number, formData: FormData) =>
    api.put(`/admin/diary/lab-tests/${entryId}`, formData),

  deleteLabTestEntry: (entryId: number) =>
    api.delete(`/admin/diary/lab-tests/${entryId}`),

  // Gamification admin endpoints
  getUsersForGamification: (search?: string) =>
    api.get('/admin/gamification/users', {
      params: { search }
    }),

  addButterflies: (userId: number, amount: number, description?: string) =>
    api.post('/admin/gamification/add-butterflies', null, {
      params: { user_id: userId, amount, description }
    }),
};

// Diary APIs
export const diaryAPI = {
  // Glucose entries
  createGlucoseEntry: (data: any) =>
    api.post('/diary/glucose', data),

  getGlucoseEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/glucose', { params }),

  // Meal entries
  createMealEntry: (data: any) =>
    api.post('/diary/meals', data),

  getMealEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/meals', { params }),

  getMealEntry: (entryId: number) =>
    api.get(`/diary/meals/${entryId}`),

  updateMealEntry: (entryId: number, data: any) =>
    api.put(`/diary/meals/${entryId}`, data),

  updateGlucoseEntry: (entryId: number, data: any) =>
    api.put(`/diary/glucose/${entryId}`, data),

  updateWeightEntry: (entryId: number, data: any) =>
    api.put(`/diary/weight/${entryId}`, data),

  updateMovementEntry: (entryId: number, data: any) =>
    api.put(`/diary/movements/${entryId}`, data),

  updateSymptomEntry: (entryId: number, data: any) =>
    api.put(`/diary/symptoms/${entryId}`, data),

  updateAbdomenEntry: (entryId: number, formData: FormData) =>
    api.put(`/diary/abdomen/${entryId}`, formData),

  updateVisitEntry: (entryId: number, formData: FormData) =>
    api.put(`/diary/visits/${entryId}`, formData),

  updateLabTestEntry: (entryId: number, formData: FormData) =>
    api.put(`/diary/lab-tests/${entryId}`, formData),

  // Phase 2: New diary entries
  createWeightEntry: (data: any) =>
    api.post('/diary/weight', data),

  getWeightEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/weight', { params }),

  createMovementEntry: (data: any) =>
    api.post('/diary/movements', data),

  getMovementEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/movements', { params }),

  createSymptomEntry: (data: any) =>
    api.post('/diary/symptoms', data),

  getSymptomEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/symptoms', { params }),

  createAbdomenEntry: (formData: FormData) => api.post('/diary/abdomen', formData),

  getAbdomenEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/abdomen', { params }),

  createVisitEntry: (formData: FormData) => api.post('/diary/visits', formData),

  getVisitEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/visits', { params }),

  createLabTestEntry: (formData: FormData) => api.post('/diary/lab-tests', formData),

  getLabTestEntries: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/lab-tests', { params }),

  // Diary progress
  getDiaryProgress: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/diary/progress', { params }),
};

// Food API
export const foodAPI = {
  searchFoods: (query: string, limit: number = 20) =>
    api.get('/foods/search', {
      params: { q: query, limit }
    }),

  createCustomFood: (data: any, force = false) =>
    api.post('/foods/custom', data, { params: { force } }),
};

// Chart APIs
export const chartAPI = {
  getGlucoseChartData: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/charts/glucose', { params }),

  getNutritionChartData: (params?: { from_date?: string; to_date?: string }) =>
    api.get('/charts/nutrition', { params }),
};


// File download API
export const fileAPI = {
  downloadFile: (fileUrl: string) =>
    api.get('/files/download', { 
      params: { file_url: fileUrl },
      responseType: 'blob'
    }),
  
  previewFile: (fileUrl: string) =>
    api.get('/files/preview', { 
      params: { file_url: fileUrl },
      responseType: 'blob'
    }),
};

// Chat APIs
export const chatAPI = {
  getConversations: () =>
    api.get('/chat/conversations'),
  
  getMessages: (conversationId: number, params?: { limit?: number; offset?: number }) =>
    api.get(`/chat/conversations/${conversationId}/messages`, { params }),
  
  uploadFile: (conversationId: number, formData: FormData) =>
    api.post(`/chat/conversations/${conversationId}/upload`, formData),
  
  createSupportConversation: () =>
    api.post('/chat/support'),

  ensureConversationWithPatient: (patientId: number) =>
    api.post(`/chat/conversations/with-patient/${patientId}`),
  
  markMessagesRead: (conversationId: number) =>
    api.post(`/chat/conversations/${conversationId}/mark-read`),
};

export default api;
